import { ResourceCrudPage } from '@/components/crud/ResourceCrudPage'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { featurePost, hidePost, restorePost } from '@/lib/p0'
import { notifyError } from '@/lib/query'
import type { ResourceRow } from '@/lib/resources'
import { EyeOff, Loader2, Pin, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

type Action = 'hide' | 'restore' | 'feature' | 'unfeature' | null

export function PostsPage() {
  const [pending, setPending] = useState<{
    action: Action
    row: ResourceRow
  } | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  async function execute() {
    if (!pending) return
    setBusy(true)
    const id = Number(pending.row.id)
    try {
      if (pending.action === 'hide') {
        await hidePost(id, reason)
        toast.success('Post hidden')
      } else if (pending.action === 'restore') {
        await restorePost(id)
        toast.success('Post restored')
      } else if (pending.action === 'feature') {
        await featurePost(id, true)
        toast.success('Post pinned')
      } else if (pending.action === 'unfeature') {
        await featurePost(id, false)
        toast.success('Post unpinned')
      }
      setReloadKey((k) => k + 1)
      setPending(null)
      setReason('')
    } catch (err) {
      notifyError('Action failed', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <ResourceCrudPage
        resource="social__post"
        classicSheet
        channelGroup="admin.activity"
        title="Feed"
        description="Live feed posts. Hide, restore, or pin content shown to the community."
        rowActions={(row) => (
          <>
            {row.is_approved ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => setPending({ action: 'hide', row })}
              >
                <EyeOff className="size-3.5" /> Hide
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPending({ action: 'restore', row })}
              >
                <RotateCcw className="size-3.5" /> Restore
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setPending({
                  action: row.is_pinned ? 'unfeature' : 'feature',
                  row
                })
              }
            >
              <Pin className="size-3.5" /> {row.is_pinned ? 'Unpin' : 'Pin'}
            </Button>
          </>
        )}
        key={reloadKey}
      />

      {pending && pending.action === 'hide' && (
        <AlertDialog open onOpenChange={(v) => !v && !busy && setPending(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Hide post #{String(pending.row.id)}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Removes from public feed immediately. Author keeps the draft and
                can edit it.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="grid gap-1.5">
              <Label htmlFor="hide-reason">Reason (optional)</Label>
              <Textarea
                id="hide-reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this being hidden?"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={busy}
                onClick={execute}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {busy && <Loader2 className="size-4 animate-spin" />}
                Hide post
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {pending &&
        (pending.action === 'restore' ||
          pending.action === 'feature' ||
          pending.action === 'unfeature') && (
          <AlertDialog
            open
            onOpenChange={(v) => !v && !busy && setPending(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {pending.action === 'restore' &&
                    `Restore post #${String(pending.row.id)}?`}
                  {pending.action === 'feature' &&
                    `Pin post #${String(pending.row.id)}?`}
                  {pending.action === 'unfeature' &&
                    `Unpin post #${String(pending.row.id)}?`}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {pending.action === 'restore' &&
                    "It'll appear in the public feed again."}
                  {pending.action === 'feature' &&
                    "It'll show at the top of the feed for relevant users."}
                  {pending.action === 'unfeature' &&
                    "It'll return to its normal position in the feed."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                <AlertDialogAction disabled={busy} onClick={execute}>
                  {busy && <Loader2 className="size-4 animate-spin" />}
                  Confirm
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
    </>
  )
}
